// Parse a FAM LCL tariff workbook into a tier map for the LCL Tariff Tiers
// import. Mirrors the validated Python loader (~/c5/enrich_bas_tiers.py
// load_excel): scans every sheet, finds the header row (the one containing
// 'ORIGEM'), locates columns by label, and parses each CBM band's UPPER bound
// straight from its header ("X - Y CBM" -> Y). Handles the 8-band (ASIA) and
// 5-band (EUROPE/AMERICAS) tariffs and any column shift between sheets.
//
// Output tierMap: { rawOriginName: { bands:[..], uppers:[..|null], currency, tt } }
// keyed by the raw Excel origin string (the server normalizes + matches).
import * as XLSX from 'xlsx';

const BAND_RE = /(\d+)\s*-\s*(\d+)\s*CBM/i;

function cellStr(v) {
    return v === undefined || v === null ? '' : String(v);
}

function snorm(s) {
    return String(s || '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Alternate (Portuguese/English) spellings so a header like "FREIGHT LINKS -
// SINGAPURA" anchors to the route origin "Singapore". Each origin token expands
// to these extra forms when matching headers.
const ANCHOR_SYN = {
    singapore: ['singapura'], istanbul: ['istambul'], antwerp: ['antuerpia'],
    lisbon: ['lisboa'], gothenburg: ['gothenborg'], belfast: ['belfest'],
    kolkata: ['calcutta'], hamburg: ['hamburgo'], london: ['londres'],
    genoa: ['genova'], geneva: ['geneve'],
};

function originTokens(n) {
    const out = [n];
    if (ANCHOR_SYN[n]) out.push(...ANCHOR_SYN[n]);
    for (const [k, vs] of Object.entries(ANCHOR_SYN)) if (vs.includes(n)) out.push(k);
    return out;
}

// Map a free-text surcharge name to a canonical Monada charge title + section.
// Returns null for names we don't recognize (skipped rather than guessed).
const SURCHARGE_MAP = [
    [/non\s*stack/i, 'NON STACKABLE', 'Origin'],
    [/\bcfs\b|container freight/i, 'CONTAINER FREIGHT STATION', 'Origin'],
    [/\bvgm\b|verified gross/i, 'VERIFIED GROSS MASS', 'Origin'],
    [/\bhbl\b/i, 'HBL FEE', 'Origin'],
    // DOC/documentation fee is the same charge CargoFive labels "BL FEE"
    [/\bbl fee\b|\bb\/l\b|\bdoc\b|documentation/i, 'BL FEE', 'Origin'],
    [/handling/i, 'HANDLING CHARGE', 'Origin'],
    [/solas/i, 'SOLAS FEE', 'Origin'],
    [/un[- ]?stuff|stuffing/i, 'UN-STUFFING', 'Origin'],
    [/equip|export transfer/i, 'EQUIPMENT TRANSFER', 'Origin'],
    [/\bams\b|manifest/i, 'ADVANCE MANIFEST SUBMISSION', 'Origin'],
    [/\bthc\b|terminal handling/i, 'TERMINAL HANDLING ORIGIN', 'Origin'],
    [/customs|clearance/i, 'CUSTOMS CLEARANCE', 'Origin'],
    [/security/i, 'CARRIER SECURITY', 'Origin'],
    [/\bsed\b/i, 'SED', 'Origin'],
    [/\bimo\b|dangerous/i, 'DANGEROUS CARGO', 'Origin'],
    [/\befs\b|emergency fuel/i, 'EMERGENCY FUEL', 'Freight Surcharge'],
];

function canonicalCharge(name) {
    for (const [re, title, section] of SURCHARGE_MAP) if (re.test(name)) return { title, section };
    return null;
}

// Parse one surcharge prose line, e.g.:
//   "CFS fee: USD 15,00 w/m - min USD 15 / max USD 50 per BL"
//   "BL FEE: USD 10,00 per BL"   "HANDLING: USD 30"   "IMO: USD 25 W/M - MIN USD 75"
function parseSurchargeLine(text) {
    const m = text.match(/^\s*([A-Za-z][A-Za-z .\/&-]*?)\s*:\s*(USD|EUR|R\$)?\s*([\d]+(?:[.,]\d+)?)\s*(.*)$/);
    if (!m) return null;
    const charge = canonicalCharge(m[1]);
    if (!charge) return null;
    const currency = (m[2] || 'USD').replace('R$', 'BRL').toUpperCase();
    const value = parseFloat(m[3].replace(',', '.'));
    if (!Number.isFinite(value)) return null;
    const rest = m[4] || '';
    const perUnit = /w\/m|per\s*ton|per\s*m3|per\s*cbm/i.test(rest);   // else flat (per BL/shipment)
    const minM = rest.match(/min\.?\s*(?:USD|EUR)?\s*([\d]+(?:[.,]\d+)?)/i);
    const min = minM ? parseFloat(minM[1].replace(',', '.')) : 0;
    return { ...charge, type: perUnit ? 'per-unit-type' : 'flat', value, min, currency };
}

// Extract per-origin surcharges from the prose blocks scattered in the sheets.
// Anchors each surcharge to an origin only when a header cell in the same column
// (above it) explicitly contains a known route origin name. grids: [{name, grid}].
function extractSurcharges(grids, originNames) {
    const origins = originNames.map(o => ({ raw: o, n: snorm(o) })).filter(o => o.n.length >= 3);
    const out = {};            // originNorm -> { title -> charge }
    const orphans = [];        // surcharge lines with no origin header: { sheet, col, row, title }
    for (const { name, grid } of grids) {
        const nrows = grid.length;
        const ncols = grid.reduce((m, r) => Math.max(m, (r || []).length), 0);
        for (let c = 0; c < ncols; c++) {
            // anchors in this column: row -> originNorm (header cells with " - " naming a known origin)
            const anchors = [];
            for (let r = 0; r < nrows; r++) {
                const txt = cellStr((grid[r] || [])[c]);
                if (!/[-–]/.test(txt)) continue;
                const cn = snorm(txt);
                const hit = origins.filter(o => originTokens(o.n).some(t => cn.includes(t))).sort((a, b) => b.n.length - a.n.length)[0];
                if (hit) anchors.push({ row: r, origin: hit.n });
            }
            for (let r = 0; r < nrows; r++) {
                const sc = parseSurchargeLine(cellStr((grid[r] || [])[c]));
                if (!sc) continue;
                let origin = null;
                for (const a of anchors) { if (a.row < r) origin = a.origin; else break; }
                if (!origin) { orphans.push({ sheet: name, col: c, row: r, title: sc.title }); continue; }
                (out[origin] = out[origin] || {})[sc.title] = sc;
            }
        }
    }
    return { byOrigin: out, orphans };
}

// Group orphan surcharge lines (no origin header) into blocks by sheet+column,
// for a clear "you have unlabeled surcharges" warning.
function groupOrphans(orphans) {
    const by = {};
    for (const o of orphans) {
        const k = `${o.sheet}|${o.col}`;
        (by[k] = by[k] || { sheet: o.sheet, col: o.col, rows: [], titles: [] });
        by[k].rows.push(o.row);
        if (!by[k].titles.includes(o.title)) by[k].titles.push(o.title);
    }
    return Object.values(by).map(b => ({
        sheet: b.sheet,
        column: XLSX.utils.encode_col(b.col),
        rows: `${Math.min(...b.rows) + 1}-${Math.max(...b.rows) + 1}`,
        titles: b.titles,
    }));
}

export function parseTariffWorkbook(arrayBuffer) {
    const wb = XLSX.read(arrayBuffer, { type: 'array' });
    const tierMap = {};
    const sheets = [];
    const allGrids = [];   // every sheet, for surcharge prose extraction

    for (const name of wb.SheetNames) {
        const ws = wb.Sheets[name];
        if (!ws) continue;
        // Force the grid origin to A1 so row/column indices are ABSOLUTE (sheets
        // whose used range starts at B1 would otherwise shift every reference).
        // blankrows:true keeps row indices aligned with the actual Excel rows.
        const ref = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']) : null;
        const opts = { header: 1, raw: true, blankrows: true };
        if (ref) { ref.s.r = 0; ref.s.c = 0; opts.range = ref; }
        const grid = XLSX.utils.sheet_to_json(ws, opts);
        allGrids.push({ name, grid });

        // find header row containing 'ORIGEM'
        let hdrIdx = -1;
        for (let i = 0; i < Math.min(grid.length, 10); i++) {
            if ((grid[i] || []).some(c => cellStr(c).trim().toUpperCase() === 'ORIGEM')) { hdrIdx = i; break; }
        }
        if (hdrIdx < 0) continue;

        // densify (sheet_to_json can return sparse arrays with holes)
        const hdr = Array.from(grid[hdrIdx], c => cellStr(c));
        const up = hdr.map(c => c.trim().toUpperCase());
        const col = (prefix) => up.findIndex(c => c.startsWith(prefix));
        const ciO = col('ORIGEM');
        const ciCur = col('MOEDA');
        const ciDest = up.findIndex(c => c.startsWith('DESTIN'));
        const ciAgent = col('AGENTE');
        const ciVia = col('VIA');
        const ciEfs = col('EFS');
        const ciTt = up.findIndex(c => c.startsWith('TT') || c.startsWith('TRANSIT'));

        const bandCols = [];   // { idx, upper }
        hdr.forEach((c, j) => {
            if (c.toUpperCase().includes('CBM')) {
                const m = BAND_RE.exec(c);
                bandCols.push({ idx: j, upper: m ? parseInt(m[2], 10) : null });
            }
        });
        if (ciO < 0 || bandCols.length < 2) continue;

        let count = 0;
        for (let r = hdrIdx + 1; r < grid.length; r++) {
            const row = grid[r] || [];
            const origem = cellStr(row[ciO]).trim();
            if (!origem) continue;
            const bands = bandCols.map(b => parseFloat(row[b.idx]));
            if (bands.some(v => !Number.isFinite(v))) continue;   // not a data row
            const at = (ci) => (ci >= 0 ? cellStr(row[ci]).trim() || null : null);
            tierMap[origem] = {
                origin: origem,
                bands,
                uppers: bandCols.map(b => b.upper),
                currency: ciCur >= 0 ? cellStr(row[ciCur]).trim() || null : null,
                tt: ciTt >= 0 ? row[ciTt] : null,
                // captured for the Excel-only (no-JSON) path:
                destination: at(ciDest),
                agent: at(ciAgent),
                via: at(ciVia),
                efs: ciEfs >= 0 ? row[ciEfs] : null,
            };
            count++;
        }
        sheets.push({ name, routes: count });
    }

    const { byOrigin: surchargesByOrigin, orphans } = extractSurcharges(allGrids, Object.keys(tierMap));
    const surchargeRouteCount = Object.keys(surchargesByOrigin).length;
    const unlabeledBlocks = groupOrphans(orphans);

    return { tierMap, sheets, routeCount: Object.keys(tierMap).length, surchargesByOrigin, surchargeRouteCount, unlabeledBlocks };
}
